import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import MovieCard from '@/components/base/MovieCard';
import ShareDialog from '@/components/base/ShareDialog';
import EmptyState from '@/components/base/EmptyState';
import { getCategory, getMovieById, getRelatedMovies, getCategoryName, formatViews } from '@/lib/queries';
import { useFavorites } from '@/hooks/useFavorites';
import { useSeo } from '@/hooks/useSeo';

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const movie = getMovieById(id ?? '');
  const category = movie ? getCategory(movie.category) : undefined;
  const { isFavorite, toggle } = useFavorites();
  const [shareOpen, setShareOpen] = useState(false);
  const [feedback, setFeedback] = useState('');

  useEffect(() => {
    setFeedback('');
    setShareOpen(false);
  }, [id]);

  useSeo({
    title: movie ? `${movie.title} - 日韩成人电影详情` : '影片不存在 - 日韩成人电影',
    description: movie
      ? `${movie.title}（${movie.originalTitle}）介绍、演员、导演及相关影片推荐。${movie.region}${movie.year}年作品，片长 ${movie.duration} 分钟，类型：${getCategoryName(movie.category)}。`
      : '该影片条目不存在，请返回首页浏览其他作品。',
    keywords: movie
      ? `${movie.title},${movie.originalTitle},${getCategoryName(movie.category)},${movie.tags.join(',')},日韩成人电影`
      : '日韩成人电影,影片详情',
    image: movie?.cover,
    type: 'article',
  });

  if (!movie) {
    return (
      <div className="mx-auto max-w-content px-4 py-16 md:px-6 md:py-24">
        <EmptyState
          icon="ri-film-line"
          title="没有找到这部影片"
          description="影片条目可能已经下架，或者链接地址有误。你可以返回首页，或者用搜索功能查找其他作品。"
          primaryLabel="返回首页"
          primaryTo="/"
        />
      </div>
    );
  }

  const favorited = isFavorite(movie.id);
  const related = getRelatedMovies(movie, 4);
  const metaRows = [
    { label: '导演', value: movie.director },
    { label: '主演', value: movie.cast.join(' / ') },
    { label: '地区', value: movie.region },
    { label: '年份', value: `${movie.year} 年` },
    { label: '片长', value: `${movie.duration} 分钟` },
    { label: '类别', value: getCategoryName(movie.category) },
  ];

  const handleFavorite = () => {
    const next = toggle(movie.id);
    setFeedback(next ? '已加入收藏，可在「我的收藏」中查看' : '已取消收藏');
  };

  return (
    <>
      <section className="relative overflow-hidden border-b border-background-800/80">
        <div className="absolute inset-0">
          <img
            src={movie.cover}
            alt=""
            aria-hidden="true"
            className="h-full w-full object-cover object-top opacity-25 blur-2xl"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background-950/80 via-background-950/90 to-background-950" />
        </div>

        <div className="relative mx-auto max-w-content px-4 pb-10 pt-6 md:px-6 md:pb-14 md:pt-8">
          <Breadcrumb
            items={[
              { label: '分类', to: `/category/${movie.category}` },
              { label: getCategoryName(movie.category), to: `/category/${movie.category}` },
              { label: movie.title },
            ]}
          />

          <div className="mt-6 grid grid-cols-1 gap-7 md:grid-cols-[220px_1fr] md:gap-8 lg:grid-cols-[260px_1fr] lg:gap-10">
            <div className="w-[190px] shrink-0 sm:w-[220px] md:w-full">
              <div className="overflow-hidden rounded-lg border border-background-700 bg-background-900">
                <img
                  src={movie.cover}
                  alt={`${movie.title} 影片封面`}
                  title={`${movie.title} - 日韩成人电影详情`}
                  className="h-auto w-full object-cover object-top"
                />
              </div>
              <div className="mt-3 flex items-center justify-between rounded-md border border-background-800 bg-background-900/80 px-3 py-2">
                <span className="text-[11px] text-foreground-500">站内评分</span>
                <span className="flex items-center gap-1 font-label text-sm font-semibold text-accent-400">
                  <i className="ri-star-fill text-xs" />
                  {movie.rating.toFixed(1)}
                </span>
              </div>
            </div>

            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <Link
                  to={`/category/${movie.category}`}
                  className="rounded bg-primary-600/90 px-2 py-1 text-[11px] text-background-50 transition-colors hover:bg-primary-500"
                >
                  {getCategoryName(movie.category)}
                </Link>
                <span className="rounded border border-background-700 px-2 py-1 text-[11px] text-foreground-400">
                  {movie.region}
                </span>
                <span className="rounded border border-background-700 px-2 py-1 font-label text-[11px] tracking-wide text-foreground-400">
                  {movie.year}
                </span>
                <span className="rounded border border-background-700 px-2 py-1 text-[11px] text-foreground-400">
                  {movie.duration} 分钟
                </span>
              </div>

              <h1 className="mt-4 text-3xl font-black leading-tight tracking-tight text-foreground-50 md:text-4xl lg:text-5xl">
                {movie.title}
              </h1>
              <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 font-label text-xs uppercase tracking-[0.16em] text-foreground-500">
                <span>{movie.originalTitle}</span>
                <span className="h-3 w-px bg-background-700" />
                <span>{movie.englishTitle}</span>
              </div>

              <p className="mt-5 max-w-2xl text-sm leading-relaxed text-foreground-300 md:text-[15px]">{movie.summary}</p>

              <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-eye-line text-sm text-primary-300" />
                  {formatViews(movie.views)} 次浏览
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-heart-3-line text-sm text-primary-300" />
                  {formatViews(movie.likes)} 次收藏
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-star-line text-sm text-primary-300" />
                  导演 {movie.director}
                </span>
              </div>

              <div className="mt-7 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={handleFavorite}
                  aria-pressed={favorited}
                  className={`flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md px-4 py-2.5 text-sm font-medium transition-colors ${
                    favorited
                      ? 'border border-accent-500 bg-accent-500/10 text-accent-400'
                      : 'bg-primary-500 text-background-50 hover:bg-primary-600'
                  }`}
                >
                  <i className={`${favorited ? 'ri-heart-fill' : 'ri-heart-line'} text-base`} />
                  {favorited ? '已收藏' : '收藏这部影片'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShareOpen(true);
                    setFeedback('');
                  }}
                  className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-700 px-4 py-2.5 text-sm text-foreground-200 transition-colors hover:border-accent-500 hover:text-accent-400"
                >
                  <i className="ri-share-forward-line text-base" />
                  分享
                </button>
                <Link
                  to="/favorites"
                  className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md px-2 py-2.5 text-sm text-foreground-500 transition-colors hover:text-accent-400"
                >
                  <i className="ri-bookmark-3-line text-base" />
                  我的收藏
                </Link>
              </div>

              {feedback && (
                <p className="animate-fade-in mt-3 flex items-center gap-1.5 text-xs text-accent-400">
                  <i className="ri-checkbox-circle-line text-sm" />
                  {feedback}
                </p>
              )}
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-content px-4 py-10 md:px-6 md:py-14">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_320px] lg:gap-14">
          <article className="min-w-0">
            <h2 className="text-xl font-bold text-foreground-50 md:text-2xl">剧情简介</h2>
            <div className="mt-4 space-y-4">
              {movie.synopsis.map((paragraph, index) => (
                <p key={index} className="text-sm leading-[1.9] text-foreground-400 md:text-[15px]">
                  {paragraph}
                </p>
              ))}
            </div>

            <div className="mt-8 rounded-lg border border-background-800 bg-background-900/60 p-4 md:p-5">
              <div className="flex items-start gap-3">
                <i className="ri-information-line mt-0.5 text-lg text-accent-500" />
                <p className="text-xs leading-relaxed text-foreground-500">
                  本条目的影片名称、演职人员与剧情均为演示用虚构内容，仅用于展示网站的影片资料结构。本站不提供任何播放、下载或交易入口。
                </p>
              </div>
            </div>

            <div className="mt-9">
              <h2 className="text-xl font-bold text-foreground-50 md:text-2xl">分类与标签</h2>
              <div className="mt-4 flex flex-wrap gap-2">
                <Link
                  to={`/category/${movie.category}`}
                  className="flex items-center gap-1.5 rounded-md bg-primary-950 px-3 py-1.5 text-xs text-primary-200 transition-colors hover:bg-primary-900 hover:text-accent-400"
                >
                  <i className="ri-folder-line text-sm" />
                  {getCategoryName(movie.category)}
                </Link>
                {movie.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/tag/${encodeURIComponent(tag)}`}
                    className="rounded-full border border-background-700 px-3 py-1.5 text-xs text-foreground-300 transition-colors hover:border-accent-500 hover:text-accent-400"
                  >
                    # {tag}
                  </Link>
                ))}
              </div>
            </div>

            <div className="mt-10">
              <div className="flex flex-wrap items-end justify-between gap-3">
                <h2 className="text-xl font-bold text-foreground-50 md:text-2xl">相关推荐</h2>
                {category && (
                  <Link
                    to={`/category/${category.slug}`}
                    className="flex items-center gap-1 text-xs text-foreground-500 transition-colors hover:text-accent-400"
                  >
                    更多{category.name}
                    <i className="ri-arrow-right-line text-sm" />
                  </Link>
                )}
              </div>
              <p className="mt-2 text-xs text-foreground-600">依据分类与标签的重合度推荐，同分类优先。</p>
              <div className="mt-5 grid grid-cols-2 gap-x-4 gap-y-7 md:grid-cols-4 md:gap-x-5">
                {related.map((item) => (
                  <MovieCard key={item.id} movie={item} />
                ))}
              </div>
            </div>
          </article>

          <aside className="w-full">
            <div className="rounded-lg border border-background-800 bg-background-900 p-5">
              <h2 className="flex items-center gap-2 text-sm font-semibold text-foreground-100">
                <i className="ri-file-list-3-line text-base text-accent-500" />
                影片资料
              </h2>
              <dl className="mt-4 space-y-3">
                {metaRows.map((row) => (
                  <div key={row.label} className="flex items-start justify-between gap-4 border-b border-background-800 pb-3 last:border-0 last:pb-0">
                    <dt className="shrink-0 text-xs text-foreground-600">{row.label}</dt>
                    <dd className="text-right text-xs leading-relaxed text-foreground-200">{row.value}</dd>
                  </div>
                ))}
              </dl>
            </div>

            <div className="mt-4 rounded-lg border border-background-800 bg-background-900 p-5">
              <h2 className="text-sm font-semibold text-foreground-100">同标签影片</h2>
              <div className="mt-4 space-y-3">
                {movie.tags.slice(0, 3).map((tag) => (
                  <Link
                    key={tag}
                    to={`/tag/${encodeURIComponent(tag)}`}
                    className="group flex cursor-pointer items-center justify-between rounded-md border border-background-800 px-3 py-2.5 transition-colors hover:border-primary-600"
                  >
                    <span className="text-xs text-foreground-300 transition-colors group-hover:text-accent-400">
                      # {tag}
                    </span>
                    <i className="ri-arrow-right-s-line text-sm text-foreground-600" />
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </div>

      <ShareDialog open={shareOpen} title={movie.title} onClose={() => setShareOpen(false)} />
    </>
  );
}